/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Adjusts the css top property of an element such that it sticks appropriately based on the scroll
 * direction. The container is assumed to be position: sticky, with top: 0 (or whatever). When
 * scrolling down, it'll stick to the bottom of the container, when scrolling up it'll stick to
 * the top of the container.
 */
console.log('🔧 Sticky Scroll Direction script loaded');

// Check if element exists in DOM
document.addEventListener('DOMContentLoaded', () => {
  const stickyElement = document.querySelector('sticky-scroll-direction');
  console.log('🔍 Checking for sticky-scroll-direction element:', stickyElement);
  if (!stickyElement) {
    console.warn('⚠️ sticky-scroll-direction element not found in DOM. Check if stick_on_scroll setting is enabled.');
  }
});

if (!customElements.get('sticky-scroll-direction')) {
  console.log('✅ Registering sticky-scroll-direction custom element');
  
  class StickyScrollDirection extends HTMLElement {
    connectedCallback() {
      console.log('🎯 StickyScrollDirection connected to DOM');
      this.init();
    }

    init() {
      console.log('🚀 Initializing StickyScrollDirection');
      
      const headerIsSticky = document.querySelector('store-header[data-is-sticky="true"]');
      console.log('  - Header is sticky:', !!headerIsSticky);
      
      this.headerHeight = Number.parseInt(
        getComputedStyle(this.parentElement).getPropertyValue('--header-height').replace('px', ''),
        10
      );
      console.log('  - Header height:', this.headerHeight);

      this.container = this.firstElementChild;
      console.log('  - Container element:', this.container);
      console.log('  - Container class:', this.container?.className);
      
      this.currentTop = Number.parseInt(
        document.documentElement.scrollTop + this.getBoundingClientRect().top - this.offsetTop,
        10
      );
      this.defaultTop = headerIsSticky ? parseInt(this.headerHeight + 30, 10) : 48;
      console.log('  - Default top offset:', this.defaultTop);
      
      this.scrollY = window.scrollY;
      this.minStickySize = this.dataset.minStickySize;
      console.log('  - Min sticky size:', this.minStickySize);
      console.log('  - Theme object exists:', typeof theme !== 'undefined');
      
      // Initialize with sticky positioning immediately
      this.container.style.position = 'sticky';
      this.container.style.top = `${this.defaultTop}px`;
      this.container.style.maxHeight = 'none';
      this.container.style.overflowY = 'visible';
      this.isStickyActive = true;
      console.log('  - Initial position set to: sticky');

      this.toggleListeners();
      window.addEventListener('on:breakpoint-change', this.toggleListeners.bind(this));
      console.log('✅ Initialization complete');
    }

    /**
     * Either inits or destroys the component based on screen size
     */
    toggleListeners() {
      console.log('🔄 Toggle Listeners called');
      console.log('  - minStickySize:', this.minStickySize);
      
      // Define breakpoints
      const breakpoints = {
        md: '(min-width: 769px)',
        lg: '(min-width: 1024px)'
      };
      
      let shouldActivate = false;
      
      if (typeof theme !== 'undefined' && theme.mediaMatches) {
        console.log('  - Using theme.mediaMatches');
        console.log('  - theme.mediaMatches:', theme.mediaMatches);
        shouldActivate = (this.minStickySize === 'lg' && theme.mediaMatches.lg)
          || (this.minStickySize === 'md' && theme.mediaMatches.md);
      } else {
        console.warn('  ⚠️ Theme object not found! Using window.matchMedia fallback');
        const mediaQuery = breakpoints[this.minStickySize] || breakpoints.md;
        console.log('  - Media query:', mediaQuery);
        shouldActivate = window.matchMedia(mediaQuery).matches;
        console.log('  - Matches:', shouldActivate);
      }
      
      if (shouldActivate) {
        console.log('  ✅ Media query matches - adding listeners');
        this.addListeners();
      } else {
        console.log('  ❌ Media query does not match - removing listeners');
        this.removeListeners();
      }
    }

    /**
     * Init the scroll watching
     */
    addListeners() {
      console.log('📡 Adding scroll listeners');
      this.scrollListener = this.scrollListener || this.handleScroll.bind(this);
      this.containerScrollListener = this.containerScrollListener || this.handleScroll.bind(this);
      
      window.addEventListener('scroll', this.scrollListener);
      console.log('  - Scroll listener attached to window');
      
      // Also listen to scroll events on the container itself for internal scrolling
      this.container.addEventListener('scroll', this.containerScrollListener);
      console.log('  - Scroll listener attached to container');

      this.scrollListener();
      console.log('  - Initial scroll handler called');

      // Set the height of the parent to the total height of other elements
      if (this.parentElement.dataset.stickyHeightElems) {
        this.debouncedSetStickyHeights = this.debouncedSetStickyHeights
          || debounce(this.setStickyHeight.bind(this), 500);
        window.addEventListener('resize', this.debouncedSetStickyHeights);

        if ('MutationObserver' in window) {
          this.stickyHeightElems = this.parentElement.dataset.stickyHeightElems;
          this.observer = new MutationObserver(this.debouncedSetStickyHeights);
          document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
            this.observer.observe(elem, {
              childList: true,
              attributes: true,
              subtree: true
            });
          });
        }

        this.setStickyHeight();
      }

      // Listen for the opening of any disclosures, so their contents can be scrolled to if needed
      this.disclosures = this.querySelectorAll('details');
      if (this.disclosures) {
        this.disclosureChangeHandler = this.disclosureChangeHandler
          || this.handleDisclosureChange.bind(this);
        this.disclosures.forEach((disclosure) => {
          disclosure.addEventListener('transitionend', this.disclosureChangeHandler);
        });
      }
    }

    /**
     * Handle the opening/closing of disclosures when they extend beyond the viewport
     * @param {object} evt - Event object.
     */
    handleDisclosureChange(evt) {
      if (evt.target.classList.contains('disclosure__panel')) {
        const summaryElem = evt.target.closest('.disclosure').querySelector('summary');
        if (!theme.elementUtil.isInViewport(summaryElem)) {
          window.scrollTo({
            behavior: 'smooth',
            top: summaryElem.getBoundingClientRect().top
              - document.body.getBoundingClientRect().top - this.headerHeight - 20
          });
        }
      }
    }

    /**
     * Destroy listeners, reset css attributes
     */
    removeListeners() {
      this.container.style.top = '';
      this.container.style.position = '';
      this.container.style.maxHeight = '';
      this.container.style.overflowY = '';
      
      window.removeEventListener('scroll', this.scrollListener);
      
      if (this.containerScrollListener) {
        this.container.removeEventListener('scroll', this.containerScrollListener);
      }

      if (this.observer) {
        this.observer.disconnect();
        window.removeEventListener('resize', this.debouncedSetStickyHeights);
      }

      if (this.disclosures) {
        this.disclosures.forEach((disclosure) => {
          disclosure.removeEventListener('transitionend', this.disclosureChangeHandler);
        });
      }
    }

    /**
     * Calculates the heights of certain elements that the height of the sticky element depends on
     */
    setStickyHeight() {
      let totalHeight = 0;
      document.querySelectorAll(this.stickyHeightElems).forEach((elem) => {
        totalHeight += elem.getBoundingClientRect().height;
      });
      this.parentElement.style.setProperty('--sticky-height', `${parseInt(totalHeight, 10)}px`);
    }

    /**
     * Updates the position based on scroll
     * 
     * Behavior:
     * 1. Product-info starts sticky immediately (top pinned to header)
     * 2. Stays sticky until product-media bottom aligns with product-info bottom
     * 3. When aligned, deactivates sticky so both scroll together naturally
     * 4. No height recalculation - only position property changes
     */
    handleScroll() {
      const productMediaElem = document.getElementById('product-media');
      
      console.log('=== STICKY SCROLL DEBUG ===');
      console.log('Product Media Element:', productMediaElem);
      
      if (!productMediaElem) {
        console.warn('⚠️ Product-media element not found! Keeping sticky behavior.');
        return;
      }

      // Get measurements
      const productMediaRect = productMediaElem.getBoundingClientRect();
      const productMediaBottom = productMediaRect.bottom;
      const productMediaBottomAbsolute = productMediaBottom + window.scrollY;
      
      const containerRect = this.container.getBoundingClientRect();
      const containerBottomAbsolute = containerRect.bottom + window.scrollY;
      
      // Check if product-media bottom has aligned with product-info bottom
      const bottomsAligned = Math.abs(productMediaBottomAbsolute - containerBottomAbsolute) < 10;
      
      console.log('📏 Measurements:');
      console.log('  - Product Media Bottom (absolute):', productMediaBottomAbsolute);
      console.log('  - Container bottom (absolute):', containerBottomAbsolute);
      console.log('  - Distance between bottoms:', productMediaBottomAbsolute - containerBottomAbsolute);
      console.log('  - Bottoms aligned:', bottomsAligned);
      console.log('  - Is Sticky Active:', this.isStickyActive);
      
      // Deactivate sticky when bottoms align so both can scroll together
      if (bottomsAligned && this.isStickyActive) {
        console.log('🎯 BOTTOMS ALIGNED - Deactivating sticky, both scroll together now');
        this.container.style.position = 'relative';
        this.container.style.top = '0';
        this.isStickyActive = false;
      } else if (!bottomsAligned && !this.isStickyActive) {
        // If scrolled back up and bottoms no longer aligned, reactivate sticky
        console.log('⏪ REACTIVATING STICKY - Bottoms no longer aligned');
        this.container.style.position = 'sticky';
        this.container.style.top = `${this.defaultTop}px`;
        this.isStickyActive = true;
      } else if (this.isStickyActive) {
        console.log('  → Sticky active, waiting for alignment');
      } else {
        console.log('  → Both scrolling together');
      }
      
      console.log('Current container styles:');
      console.log('  - position:', this.container.style.position);
      console.log('  - top:', this.container.style.top);
      console.log('========================\n');
      
      this.scrollY = window.scrollY;
    }
  }

  customElements.define('sticky-scroll-direction', StickyScrollDirection);
}
